import { config } from 'config';
import { User, UserRole } from 'shared/types';
import { UserCompanyVerifications } from 'shared/services/userVerificationsApi';

const API_BASE_URL = config.apiUrl;

/**
 * Decode JWT payload (no signature verification) so we can check `exp`
 * before accepting a refreshed token. Mirrors the helper inside
 * `apiClient.ts` — kept inline here to avoid pulling apiClient into this
 * module's dependency graph (authService is consumed at bootstrap).
 */
const decodeJwt = (token: string): { exp?: number } | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    return JSON.parse(atob(parts[1]));
  } catch {
    return null;
  }
};

/**
 * Persist a rotated token from `X-Refreshed-Token` headers on auth
 * responses. The BE rotates the JWT on certain flows (2FA verify, /me,
 * document-access verify) so the client should always read this header
 * and overwrite localStorage when a fresher token is offered — otherwise
 * the user keeps using an old token that may expire mid-session.
 */
const applyRefreshedTokenHeader = (response: Response): void => {
  const refreshed = response.headers.get('X-Refreshed-Token');
  if (!refreshed) return;
  const payload = decodeJwt(refreshed);
  const stillValid = payload?.exp && payload.exp > Math.floor(Date.now() / 1000) + 60;
  if (stillValid) {
    try {
      localStorage.setItem('token', refreshed);
    } catch {
      // localStorage may throw in private-browsing modes — silently skip.
    }
  }
};

/**
 * Shape of an entry inside a login/2fa response's `companies[]` payload.
 * Mirrors `User['companies'][number]` but the BE may populate `company`
 * either as an id string or a small embedded object.
 */
export interface AuthCompanyEntry {
  companyId?: string;
  companyName?: string;
  companyRuc?: string;
  role?: UserRole | string;
  isActive?: boolean;
  startDate?: string;
  permissions?: string[];
  isExternal?: boolean;
  homeCompanyId?: string;
  homeCompanyName?: string;
  viaCompanyId?: string;
  viaCompanyName?: string;
  company?: string | {
    _id: string;
    name: string;
    companyType?: 'cliente' | 'proveedor';
    settings?: { modules?: Record<string, boolean> };
  };
  joinedAt?: string;
}

/**
 * Minimal active-company payload surfaced on login responses. Full company
 * detail is fetched separately via `companyApi.getById`.
 */
export interface AuthActiveCompany {
  _id: string;
  name: string;
  ruc?: string;
  taxId?: string;
  address?: string;
  phone?: string;
  email?: string;
  industry?: string;
  userCount?: number;
  isActive?: boolean;
  companyType?: 'cliente' | 'proveedor';
  settings?: {
    notificationDays?: number[];
    requiredCourses?: string[];
    modules?: Record<string, boolean>;
  };
  createdAt?: string | Date;
  updatedAt?: string | Date;
}

/**
 * Per-company pending counts surfaced on `CompanyOption.pending`. The BE
 * returns `null` when the lookup failed (so the UI knows to render
 * nothing rather than "0 pendientes"). When present, `total` mirrors the
 * sum of the four buckets and is the value the chip should display.
 */
export interface PendingCounts {
  verifications: number;
  evaluations: number;
  permits: number;
  serviceCards: number;
  total: number;
}

// Company option for multi-company users
export interface CompanyOption {
  id: string;
  name: string;
  role: string;
  /** True when this is the user's own company (non-external). External
   *  entries are access links into a client/provider's tenant. */
  isHome?: boolean;
  /** True for a borrowed/lent-in seat (a provider worker's access to a
   *  client company). See CompanySelector's sort — these are shown ahead
   *  of the user's own company, since a provider admin lent to a client
   *  almost always wants the client, not their own company's admin view. */
  isExternal?: boolean;
  /** Only meaningful for the user's own (non-external) membership in a
   *  proveedor company — `false` means that company has no active plan and
   *  should be shown disabled here rather than picked normally. `undefined`
   *  for every other entry (client companies, external/lent-in seats). */
  hasOwnActivePlan?: boolean;
  twoFactorMethod: 'email' | 'authenticator' | null;
  /** Pending workload bucketed by surface. `null` when the BE lookup
   *  failed; `undefined` when the BE didn't compute it (older clients). */
  pending?: PendingCounts | null;
  /** Resolved presigned URL for the company logo (null when no logo set). */
  logoUrl?: string | null;
}

// Company Selection Required Response (multi-company users)
interface CompanySelectionRequiredResponse {
  success: true;
  requiresCompanySelection: true;
  message: string;
  email: string;
  companies: CompanyOption[];
}

// 2FA Required Response (updated for single-method flow)
interface TwoFactorRequiredResponse {
  success: true;
  requires2FA: true;
  message: string;
  email: string; // Masked email like "ze***@gmail.com"
  expiresInMinutes?: number; // Only for email method
  emailSent?: boolean; // Only for email method
  // Single method (NEW)
  requiredMethod: 'email' | 'authenticator';
  companyRequiredMethod: 'email' | 'authenticator';
  userHasTotp: boolean;
  reason: string;
  // Grace period info (NEW)
  isGracePeriod: boolean;
  gracePeriodEndsAt: string | null;
  canUseEmailDuringGrace: boolean;
  // Email error info
  emailError?: boolean;
  hasBackupCodes?: boolean;
  twoFactor?: { method: string; email: string };
  // Company context
  selectedCompanyId?: string;
  // Legacy fields (for backwards compatibility)
  availableMethods?: ('authenticator' | 'email')[];
}

// Blocked user response (authenticator required but not configured)
interface AuthenticatorBlockedResponse {
  success: false;
  error: string;
  code: 'AUTHENTICATOR_REQUIRED_NOT_CONFIGURED';
  requires2FASetup: true;
  requiredMethod: 'authenticator';
}

// Direct Login Response (no 2FA)
interface DirectLoginResponse {
  success: boolean;
  token: string;
  data: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    globalRole?: string;
    companies?: AuthCompanyEntry[];
    activeCompany?: AuthActiveCompany;
    company?: {
      _id: string;
      name: string;
      ruc: string;
      address: string;
      phone: string;
      email: string;
      isActive: boolean;
      settings?: {
        notificationDays: number[];
        requiredCourses: string[];
      };
    } | null;
  };
  gracePeriod?: {
    inGracePeriod: boolean;
    daysRemaining: number;
    gracePeriodEnds: string;
    message: string;
  };
}

// Combined Login Response type
export type LoginResponse = CompanySelectionRequiredResponse | TwoFactorRequiredResponse | DirectLoginResponse;

// Verify 2FA Response
export interface Verify2FAResponse {
  success: boolean;
  token: string;
  /** Per-device trust token (valid ~3h) — stored to skip 2FA on next login. */
  deviceTrustToken?: string;
  data: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    globalRole?: string;
    companies?: AuthCompanyEntry[];
    activeCompany?: AuthActiveCompany;
    companiesVerifications?: UserCompanyVerifications[];
  };
}

// Resend 2FA Response
export interface Resend2FAResponse {
  success: boolean;
  message?: string;
  error?: string;
  waitSeconds?: number;
}

// Setup Token Response Types
export interface SetupTokenValidationResponse {
  success: boolean;
  valid: boolean;
  data?: {
    userId?: string;
    email: string;
    firstName: string;
    lastName: string;
    companyName: string;
    expiresAt: string;
  };
  error?: string;
}

// Raw API response structure (what backend actually returns)
interface SetupTokenValidationApiResponse {
  success: boolean;
  data: {
    valid: boolean;
    user: {
      email: string;
      firstName: string;
      lastName: string;
    };
    company: {
      name: string;
      allowedMethods: string[];
    };
    expiresAt: string;
  };
  error?: string;
}

export interface StartSetupWithTokenResponse {
  success: boolean;
  qrCodeUrl: string;
  manualEntryKey: string;
  setupToken: string;
}

// Raw API response for start setup
interface StartSetupWithTokenApiResponse {
  success: boolean;
  message: string;
  data: {
    qrCodeDataUrl: string;
    manualEntryKey: string;
    user: {
      email: string;
      firstName: string;
      lastName: string;
    };
    company: {
      name: string;
    };
    setupToken: string;
  };
}

export interface VerifySetupWithTokenResponse {
  success: boolean;
  message: string;
}

export interface GenerateSetupTokenResponse {
  success: boolean;
  token: string;
  expiresAt: string;
  setupUrl: string;
}

/**
 * Shape of the `/auth/me` response envelope. The BE returns the full user
 * document under `data` (or, on legacy clients, `user`). Callers consume
 * the typed `User` shape from `shared/types` plus a few platform-only
 * fields surfaced on the envelope.
 */
export interface CurrentUserResponse {
  success: boolean;
  data?: User & {
    companies?: AuthCompanyEntry[];
    activeCompany?: AuthActiveCompany;
    companiesVerifications?: UserCompanyVerifications[];
  };
  user?: User & {
    companies?: AuthCompanyEntry[];
    activeCompany?: AuthActiveCompany;
    companiesVerifications?: UserCompanyVerifications[];
  };
  error?: string;
}

// Grace Period Login Response
export interface GracePeriodLoginResponse {
  success: true;
  token: string;
  user: Partial<User> & { _id: string; email: string };
  gracePeriod: {
    inGracePeriod: true;
    daysRemaining: number;
    gracePeriodEnds: string;
    message: string;
  };
}

interface AuthError {
  success: false;
  error: string;
  waitSeconds?: number;
}

// Type guard to check if response requires company selection
export function requiresCompanySelection(response: LoginResponse): response is CompanySelectionRequiredResponse {
  return 'requiresCompanySelection' in response && response.requiresCompanySelection === true;
}

// Type guard to check if response requires 2FA
export function requires2FA(response: LoginResponse): response is TwoFactorRequiredResponse {
  return 'requires2FA' in response && response.requires2FA === true;
}

// Type guard to check if login response has grace period warning
export function hasGracePeriod(response: unknown): response is GracePeriodLoginResponse {
  if (typeof response !== 'object' || response === null) return false;
  const grace = (response as { gracePeriod?: { inGracePeriod?: boolean } }).gracePeriod;
  return grace?.inGracePeriod === true;
}

// Type guard to check if error is authenticator blocked
export function isAuthenticatorBlocked(error: unknown): error is AuthenticatorBlockedResponse {
  if (typeof error !== 'object' || error === null) return false;
  return (error as { code?: string }).code === 'AUTHENTICATOR_REQUIRED_NOT_CONFIGURED';
}

class AuthService {
  private static instance: AuthService;
  
  private constructor() {}
  
  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  async login(email: string, password: string, companyId?: string): Promise<LoginResponse> {
    try {
      const body: { email: string; password: string; companyId?: string; deviceTrustToken?: string } = { email, password };
      if (companyId) {
        body.companyId = companyId;
      }
      // Send the per-device 2FA trust token (if this device proved 2FA in the
      // last 3h) so the backend can skip the 2FA challenge.
      try {
        const trust = localStorage.getItem('deviceTrustToken');
        if (trust) body.deviceTrustToken = trust;
      } catch { /* storage unavailable */ }

      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      applyRefreshedTokenHeader(response);
      const data = await response.json();

      if (!response.ok) {
        // Check for authenticator blocked error (403)
        if (response.status === 403 && data.code === 'AUTHENTICATOR_REQUIRED_NOT_CONFIGURED') {
          const error = new Error(data.error) as Error & Partial<AuthenticatorBlockedResponse>;
          error.code = data.code;
          error.requires2FASetup = data.requires2FASetup;
          error.requiredMethod = data.requiredMethod;
          throw error;
        }
        throw new Error((data as AuthError).error || 'Error al iniciar sesión');
      }

      return data as LoginResponse;
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Error de conexión con el servidor');
    }
  }

  async getCurrentUser(token: string): Promise<CurrentUserResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      applyRefreshedTokenHeader(response);

      if (!response.ok) {
        throw new Error('Error al obtener usuario actual');
      }

      return await response.json();
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Error de conexión con el servidor');
    }
  }

  async getCurrentUserWithVerifications(): Promise<CurrentUserResponse> {
    try {
      const token = this.getStoredToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      applyRefreshedTokenHeader(response);

      if (!response.ok) {
        throw new Error('Error al obtener usuario actual');
      }

      const data = await response.json();
      return data;
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Error de conexión con el servidor');
    }
  }

  /**
   * Switch the active platform role for identities holding more than one
   * (super_admin <-> safety_staff). Returns a fresh JWT carrying the new role;
   * the caller stores it and updates auth state.
   */
  async switchGlobalRole(role: string): Promise<{
    token: string;
    role: string;
    globalRole: string;
    globalRoles: string[];
  }> {
    const token = this.getStoredToken();
    if (!token) throw new Error('No authentication token');

    const response = await fetch(`${API_BASE_URL}/auth/switch-global-role`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ role }),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.success) {
      throw new Error(data?.error || 'No se pudo cambiar de rol');
    }
    return data.data;
  }

  /**
   * Switch the active company for a multi-company (non-platform) user.
   * Returns a fresh JWT carrying the new `activeCompany` — without this,
   * a page reload (which reauthenticates with GET /auth/me using the
   * stored token) reverts to whichever company the user logged in with,
   * since the old token never reflected the switch.
   */
  async switchCompany(companyId: string): Promise<{
    token: string;
    companyId: string;
    role: string;
  }> {
    const token = this.getStoredToken();
    if (!token) throw new Error('No authentication token');

    const response = await fetch(`${API_BASE_URL}/auth/switch-company`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ companyId }),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.success) {
      throw new Error(data?.error || 'No se pudo cambiar de empresa');
    }
    return data.data;
  }

  getStoredToken(): string | null {
    return localStorage.getItem('token');
  }

  setToken(token: string): void {
    localStorage.setItem('token', token);
  }

  removeToken(): void {
    localStorage.removeItem('token');
  }

  // 2FA Methods
  async verify2FA(
    email: string,
    code: string,
    method: 'authenticator' | 'email' | 'backup' = 'email',
    companyId?: string,
    rememberDevice?: boolean
  ): Promise<Verify2FAResponse> {
    try {
      const body: { email: string; code: string; method: string; companyId?: string; rememberDevice?: boolean } = {
        email,
        code,
        method,
      };
      if (companyId) {
        body.companyId = companyId;
      }
      if (rememberDevice) {
        // Opt into the 7-day device-trust window (default is 3h).
        body.rememberDevice = true;
      }

      const response = await fetch(`${API_BASE_URL}/auth/verify-2fa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      applyRefreshedTokenHeader(response);
      const data = await response.json();

      if (!response.ok) {
        throw new Error((data as AuthError).error || 'Código de verificación inválido');
      }

      // Persist the per-device 2FA trust token so the next login (e.g. after an
      // idle logout) can skip the 2FA prompt for ~3h on this device.
      if (data?.deviceTrustToken) {
        try { localStorage.setItem('deviceTrustToken', data.deviceTrustToken); } catch { /* storage unavailable */ }
      }

      return data as Verify2FAResponse;
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Error de conexión con el servidor');
    }
  }

  async resend2FA(email: string): Promise<Resend2FAResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/resend-2fa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        // For rate limiting, include waitSeconds in the error
        if (response.status === 429 && data.waitSeconds) {
          const error = new Error(data.error || 'Por favor espera antes de solicitar un nuevo código') as Error & { waitSeconds?: number };
          error.waitSeconds = data.waitSeconds;
          throw error;
        }
        throw new Error((data as AuthError).error || 'Error al reenviar código');
      }

      return data as Resend2FAResponse;
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Error de conexión con el servidor');
    }
  }

  // ==================== 2FA SETUP TOKEN METHODS (Public endpoints) ====================

  /**
   * Validate a 2FA setup token (public endpoint)
   * Used to verify the token is valid before showing setup UI
   */
  async validateSetupToken(token: string): Promise<SetupTokenValidationResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/2fa/setup-token/${token}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const apiResponse = await response.json() as SetupTokenValidationApiResponse;

      if (!response.ok) {
        return {
          success: false,
          valid: false,
          error: apiResponse.error || 'Token inválido o expirado',
        };
      }

      // Transform API response to expected format
      // Backend returns: { success, data: { valid, user: {...}, company: {...}, expiresAt } }
      // Component expects: { success, valid, data: { email, firstName, lastName, companyName, expiresAt } }
      return {
        success: apiResponse.success,
        valid: apiResponse.data?.valid ?? false,
        data: apiResponse.data ? {
          email: apiResponse.data.user.email,
          firstName: apiResponse.data.user.firstName,
          lastName: apiResponse.data.user.lastName,
          companyName: apiResponse.data.company.name,
          expiresAt: apiResponse.data.expiresAt,
        } : undefined,
      };
    } catch (error: unknown) {
      return {
        success: false,
        valid: false,
        error: 'Error de conexión con el servidor',
      };
    }
  }

  /**
   * Start 2FA setup with a setup token (public endpoint)
   * Returns QR code and secret for authenticator app
   */
  async startSetupWithToken(token: string): Promise<StartSetupWithTokenResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/2fa/setup-with-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const apiResponse = await response.json() as StartSetupWithTokenApiResponse;

      if (!response.ok) {
        throw new Error(apiResponse.message || 'Error al iniciar configuración de 2FA');
      }

      // Transform API response to expected format
      // Backend returns: { success, data: { qrCodeDataUrl, manualEntryKey, setupToken, ... } }
      // Component expects: { success, qrCodeUrl, manualEntryKey, setupToken }
      return {
        success: apiResponse.success,
        qrCodeUrl: apiResponse.data.qrCodeDataUrl,
        manualEntryKey: apiResponse.data.manualEntryKey,
        setupToken: apiResponse.data.setupToken,
      };
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Error de conexión con el servidor');
    }
  }

  /**
   * Verify and complete 2FA setup with a token (public endpoint)
   * Verifies TOTP setup with a token
   */
  async verifySetupWithToken(token: string, code: string): Promise<VerifySetupWithTokenResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/2fa/verify-with-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, code }),
      });

      const apiResponse = await response.json();

      if (!response.ok) {
        throw new Error(apiResponse.error || 'Código de verificación inválido');
      }

      return {
        success: apiResponse.success,
        message: apiResponse.message,
      };
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Error de conexión con el servidor');
    }
  }

  /**
   * Generate a setup token for a user (requires admin authentication)
   * Used by admins to help locked-out users
   */
  async generateSetupToken(data: {
    userId: string;
    companyId: string;
    expiresInHours?: number;
    reason?: string;
  }): Promise<GenerateSetupTokenResponse> {
    try {
      const token = this.getStoredToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/auth/2fa/setup-token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      applyRefreshedTokenHeader(response);
      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.error || 'Error al generar token de configuración');
      }

      return responseData as GenerateSetupTokenResponse;
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Error de conexión con el servidor');
    }
  }

  // ==================== USER TOTP SETUP (Authenticated endpoints) ====================

  /**
   * Start TOTP setup for the current authenticated user
   * Returns QR code and manual entry key
   */
  async startUserTotpSetup(): Promise<{
    success: boolean;
    qrCodeUrl: string;
    manualEntryKey: string;
  }> {
    try {
      const token = this.getStoredToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/auth/2fa/totp/setup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      applyRefreshedTokenHeader(response);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al iniciar configuración de autenticador');
      }

      // Transform response - backend returns data.qrCode, data.qrCodeDataUrl, or data.qrCodeUrl
      return {
        success: true,
        qrCodeUrl: data.data?.qrCode || data.data?.qrCodeDataUrl || data.data?.qrCodeUrl || data.qrCode || data.qrCodeDataUrl || data.qrCodeUrl,
        manualEntryKey: data.data?.manualEntryKey || data.manualEntryKey,
      };
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Error de conexión con el servidor');
    }
  }

  /**
   * Verify TOTP setup and enable it for the current user
   */
  async verifyUserTotpSetup(code: string): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      const token = this.getStoredToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/auth/2fa/totp/verify-setup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      });

      applyRefreshedTokenHeader(response);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Código inválido');
      }

      return {
        success: true,
        message: data.message || 'Autenticador configurado correctamente',
      };
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Error de conexión con el servidor');
    }
  }

  /**
   * Get current user's 2FA status
   */
  async getUserTwoFactorStatus(): Promise<{
    totpEnabled: boolean;
    companyRequires2FA: boolean;
    requiredMethod?: 'email' | 'authenticator';
    isInGracePeriod: boolean;
    gracePeriodEndsAt?: string;
  }> {
    try {
      const token = this.getStoredToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      applyRefreshedTokenHeader(response);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al obtener estado de 2FA');
      }

      const user = data.user || data;
      const twoFactorAuth = user.twoFactorAuth || {};
      const company = user.company || {};
      const companyTwoFactor = company.twoFactor || {};

      return {
        totpEnabled: twoFactorAuth.totp?.enabled || false,
        companyRequires2FA: companyTwoFactor.enabled || false,
        requiredMethod: companyTwoFactor.method,
        isInGracePeriod: !!twoFactorAuth.gracePeriod?.active,
        gracePeriodEndsAt: twoFactorAuth.gracePeriod?.expiresAt,
      };
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Error de conexión con el servidor');
    }
  }

  // ==================== DOCUMENT ACCESS SECURITY METHODS ====================

  /**
   * Get current document access status
   * Returns whether the current session has valid document access and time remaining
   */
  async getDocumentAccessStatus(): Promise<{
    hasAccess: boolean;
    verifiedAt: string | null;
    remainingMs: number;
    remainingSeconds: number;
    windowMs: number;
    has2FAConfigured: boolean;
    preferredMethod: 'email' | 'authenticator';
  }> {
    try {
      const token = this.getStoredToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/auth/document-access/status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      applyRefreshedTokenHeader(response);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al obtener estado de acceso a documentos');
      }

      return data.data;
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Error de conexión con el servidor');
    }
  }

  /**
   * Verify 2FA for document access (step-up authentication)
   * Returns a new token with document access verification
   */
  async verifyDocumentAccess(
    code: string,
    method: 'email' | 'authenticator' = 'authenticator'
  ): Promise<{
    success: boolean;
    token: string;
    documentAccess: {
      verifiedAt: string;
      expiresAt: string;
      windowMs: number;
    };
  }> {
    try {
      const token = this.getStoredToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/auth/document-access/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code, method }),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorWithCode = new Error(data.error || 'Código de verificación inválido') as Error & { code?: string };
        errorWithCode.code = data.code;
        throw errorWithCode;
      }

      // Update stored token with new one that has document access
      if (data.token) {
        this.setToken(data.token);
      }

      return data;
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Error de conexión con el servidor');
    }
  }

  /**
   * Request email 2FA code for document access
   */
  async requestDocumentAccessCode(): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      const token = this.getStoredToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/auth/document-access/request-code`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      applyRefreshedTokenHeader(response);
      const data = await response.json();

      if (!response.ok) {
        const errorWithCode = new Error(data.error || 'Error al solicitar código') as Error & { code?: string };
        errorWithCode.code = data.code;
        throw errorWithCode;
      }

      return data;
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Error de conexión con el servidor');
    }
  }

}

export default AuthService.getInstance();